import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Search, X, ArrowLeft } from 'lucide-react'

import { api, AppLimitError } from '@/lib/api'
import { useUpgradeStore } from '@/stores/upgrade.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { Application, Cycle, ProgramTemplate } from '@/types/models'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Step = 1 | 2 | 3

interface Props {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
}

// ---------------------------------------------------------------------------
// AddApplicationModal
// ---------------------------------------------------------------------------

/**
 * AddApplicationModal — 3-step wizard to add a new application.
 *
 * Step 1: Search program templates or choose "Add manually"
 * Step 2: Preview selected program (skipped for manual)
 * Step 3: Confirm — cycle, deadline → POST /api/v1/applications
 */
export default function AddApplicationModal({ isOpen, onClose, workspaceId }: Props) {
  const queryClient = useQueryClient()
  const openUpgrade = useUpgradeStore((s) => s.open)
  const { selectedCycleId, setSelectedCycleId } = useWorkspaceStore()

  const [step, setStep] = useState<Step>(1)
  const [search, setSearch] = useState('')
  const [selectedProgram, setSelectedProgram] = useState<ProgramTemplate | null>(null)
  const [manualMode, setManualMode] = useState(false)
  const [confirmCycleId, setConfirmCycleId] = useState<string>(selectedCycleId ?? '')
  const [deadline, setDeadline] = useState('')
  const [universityName, setUniversityName] = useState('')
  const [programName, setProgramName] = useState('')
  const [error, setError] = useState('')

  // Fetch cycles
  const { data: cyclesData } = useQuery<ApiResponse<Cycle[]>>({
    queryKey: ['cycles', workspaceId],
    queryFn: () => api.get<ApiResponse<Cycle[]>>('/v1/cycles'),
    enabled: !!workspaceId,
  })
  const cycles = cyclesData?.data ?? []

  // Search program templates
  const { data: searchData, isFetching: searching } = useQuery<ApiResponse<ProgramTemplate[]>>({
    queryKey: ['program-templates', search],
    queryFn: () =>
      api.get<ApiResponse<ProgramTemplate[]>>(
        `/v1/program-templates?search=${encodeURIComponent(search)}`,
      ),
    enabled: search.trim().length >= 2,
    staleTime: 60_000,
  })
  const searchResults = searchData?.data ?? []

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<ApiResponse<Application>>('/v1/applications', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications', workspaceId] })
      handleClose()
    },
    onError: (err: unknown) => {
      if (err instanceof AppLimitError) {
        handleClose()
        openUpgrade()
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to create application.')
    },
  })

  function handleClose() {
    onClose()
    // Reset state after animation
    setTimeout(() => {
      setStep(1)
      setSearch('')
      setSelectedProgram(null)
      setManualMode(false)
      setDeadline('')
      setUniversityName('')
      setProgramName('')
      setError('')
    }, 200)
  }

  function handleSelectProgram(program: ProgramTemplate) {
    setSelectedProgram(program)
    setStep(2)
  }

  function handleAddManually() {
    setManualMode(true)
    setSelectedProgram(null)
    setStep(3)
  }

  function handleProgramConfirm() {
    if (selectedProgram) {
      setUniversityName(selectedProgram.universityName)
      setProgramName(selectedProgram.programName)
      setDeadline(selectedProgram.deadline?.slice(0, 10) ?? '')
    }
    setStep(3)
  }

  function handleConfirmSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    const uName = manualMode ? universityName : (selectedProgram?.universityName ?? universityName)
    const pName = manualMode ? programName : (selectedProgram?.programName ?? programName)
    if (!uName.trim() || !pName.trim()) {
      setError('University and program name are required.')
      return
    }
    if (!confirmCycleId) {
      setError('Please select a cycle.')
      return
    }
    createMutation.mutate({
      universityName: uName.trim(),
      programName: pName.trim(),
      cycleId: confirmCycleId,
      deadlineDate: deadline ? new Date(deadline).toISOString() : undefined,
      programTemplateId: selectedProgram?.id,
    })
  }

  const title =
    step === 1 ? 'Add Application' : step === 2 ? 'Program Preview' : 'Confirm Application'

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl focus:outline-none">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}
                  className="rounded-md p-1 text-muted-foreground hover:bg-accent"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <Dialog.Title className="text-base font-semibold text-foreground">
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-accent"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-5">
            {([1, 2, 3] as Step[]).map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  s <= step ? 'bg-brand-blue' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* STEP 1: Search */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search universities or programs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                  className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {searching && (
                <p className="text-center text-sm text-muted-foreground">Searching...</p>
              )}

              {!searching && search.trim().length >= 2 && searchResults.length === 0 && (
                <p className="text-center text-sm text-muted-foreground">No programs found.</p>
              )}

              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border">
                  {searchResults.map((program) => (
                    <button
                      key={program.id}
                      type="button"
                      onClick={() => handleSelectProgram(program)}
                      className="flex w-full flex-col px-4 py-3 text-left hover:bg-accent"
                    >
                      <span className="text-sm font-medium text-foreground">
                        {program.universityName}
                      </span>
                      <span className="text-xs text-muted-foreground">{program.programName}</span>
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={handleAddManually}
                className="w-full rounded-md border border-dashed px-4 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                + Add manually
              </button>
            </div>
          )}

          {/* STEP 2: Program preview */}
          {step === 2 && selectedProgram && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-foreground">{selectedProgram.universityName}</h3>
                <p className="text-sm text-muted-foreground">{selectedProgram.programName}</p>
              </div>
              <div className="rounded-md border bg-muted/30 p-4 space-y-2 text-sm">
                {selectedProgram.deadline && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deadline</span>
                    <span className="font-medium">
                      {new Date(selectedProgram.deadline).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {selectedProgram.essayCount !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Essays</span>
                    <span className="font-medium">{selectedProgram.essayCount}</span>
                  </div>
                )}
                {selectedProgram.lorCount !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">LORs Required</span>
                    <span className="font-medium">{selectedProgram.lorCount}</span>
                  </div>
                )}
                {selectedProgram.testRequired && selectedProgram.testRequired.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tests Required</span>
                    <span className="font-medium">{selectedProgram.testRequired.join(', ')}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleProgramConfirm}
                className="w-full rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Add This Program
              </button>
            </div>
          )}

          {/* STEP 3: Confirm */}
          {step === 3 && (
            <form onSubmit={handleConfirmSubmit} className="space-y-4">
              {manualMode && (
                <>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-muted-foreground">
                      University Name *
                    </span>
                    <input
                      type="text"
                      value={universityName}
                      onChange={(e) => setUniversityName(e.target.value)}
                      placeholder="e.g. Harvard Business School"
                      className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-muted-foreground">
                      Program Name *
                    </span>
                    <input
                      type="text"
                      value={programName}
                      onChange={(e) => setProgramName(e.target.value)}
                      placeholder="e.g. MBA"
                      className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                </>
              )}

              {!manualMode && selectedProgram && (
                <div className="rounded-md bg-muted/30 px-4 py-3">
                  <p className="font-medium text-foreground">{selectedProgram.universityName}</p>
                  <p className="text-sm text-muted-foreground">{selectedProgram.programName}</p>
                </div>
              )}

              {/* Cycle selector */}
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">Cycle *</span>
                <select
                  value={confirmCycleId}
                  onChange={(e) => {
                    setConfirmCycleId(e.target.value)
                    setSelectedCycleId(e.target.value)
                  }}
                  className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select a cycle...</option>
                  {cycles.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.season} {c.year})
                    </option>
                  ))}
                </select>
              </label>

              {/* Deadline */}
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">Deadline</span>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={createMutation.isPending}
                className="w-full rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                {createMutation.isPending ? 'Creating...' : 'Confirm'}
              </button>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
